// Volts panel: portræt, taleboble, tænke-prikker, kildechip ("Volt: Jev 0,22 s" eller "Volt på
// rygmarven"), hvad han er ved at gøre med "Jeg tager den", spørgsmål med to knapper, chips og
// fritekst til instrukser, og et sammenklappeligt tankepanel med kandidater, sandsynligheder og log.
// Markøren på kortet er et lille billede oven på scenen. Panelet er ren visning: alle valg går
// tilbage gennem handlers til controlleren (ai/controller.ts).

import type { VoltCandidate } from '../ai/candidates';
import type { Brain, Decision, VoltStats } from '../ai/controller';
import { LIMITS } from '../ai/protocol';
import type { Avoid, Mood, Policy } from '../ai/protocol';
import type { P } from '../render/geo';
import { h, setAttr, setText, toggle } from './dom';

export interface VoltPanelHandlers {
  /** "Jeg tager den": spilleren overtager Volts næste træk. */
  claim(): void;
  /** Spilleren valgte en af to muligheder, da Volt var i tvivl. */
  answer(id: string): void;
  /** En chip eller fritekst. */
  instruct(text: string, chip: boolean): void;
  /** Spilleren fjernede et af sine ønsker (× på mærket). */
  unset(what: PolicyKey): void;
}

/** Et ønske, der kan fjernes enkeltvis. */
export type PolicyKey = 'region' | 'priority' | 'save' | Avoid;

/** Billeder fra atlasset, så Volt ser ens ud på kortet og i panelet. */
export interface VoltFaces {
  volt: string;
  think: string;
  worried: string;
  blink: string;
  marker: string;
}

export const VOLT_CHIPS = ['Byg løs', 'Spar penge', 'Tag vest', 'Jeg tager stormen'] as const;

const REGION_DA: Record<string, string> = { west: 'Vest', east: 'Øst', north: 'Nord', south: 'Syd' };
const PRIORITY_DA: Record<string, string> = { cities: 'Byerne først', green: 'Grønt', safety: 'Sikkert', money: 'Sparer' };
const SAVE_DA: Record<string, string> = { storm: 'Klar til storm', new_city: 'Sparer til ny by', upgrade: 'Sparer til opgradering', battery: 'Sparer til batteri' };
const AVOID_DA: Record<Avoid, string> = {
  storm: 'Stormen er din',
  cards: 'Kortene er dine',
  build: 'Bygger ikke',
  upgrade: 'Opgraderer ikke',
  spend: 'Bruger ingen penge',
  wait: 'Bygger løs',
};

/** Spillerens aktive ønsker som korte mærker (tom liste: Volt spiller frit). */
export function policyTags(p: Policy): { key: PolicyKey; text: string }[] {
  const out: { key: PolicyKey; text: string }[] = [];
  for (const a of p.avoid) if (a === 'wait') out.push({ key: a, text: AVOID_DA[a] });
  if (p.priority !== 'balanced') out.push({ key: 'priority', text: PRIORITY_DA[p.priority] ?? p.priority });
  if (p.save_for !== 'nothing') out.push({ key: 'save', text: SAVE_DA[p.save_for] ?? p.save_for });
  if (p.focus_region !== 'all') out.push({ key: 'region', text: REGION_DA[p.focus_region] ?? p.focus_region });
  for (const a of p.avoid) if (a !== 'wait') out.push({ key: a, text: AVOID_DA[a] });
  return out;
}

/** Rygmarvens scorer som andele i procent (softmax, samme temperatur som mock-Jev). */
export function spineShares(scores: readonly number[], temp = 12): number[] {
  if (scores.length === 0) return [];
  const max = Math.max(...scores);
  const e = scores.map((v) => Math.exp((v - max) / temp));
  const sum = e.reduce((a, b) => a + b, 0);
  return e.map((v) => v / sum);
}

/** Volts beslutningslog vises kun med ?debug. */
const DEBUG_LOG = typeof location !== 'undefined' && new URLSearchParams(location.search).has('debug');

const BRAIN_NAME: Record<Brain, string> = { jev: 'Jev', rygmarv: 'rygmarv', player: 'dig' };

/** 0,22 s med dansk komma. */
export function secs(ms: number): string {
  return `${(ms / 1000).toFixed(2).replace('.', ',')} s`;
}

/** Kildechippens tekst. */
export function brainChip(by: 'jev' | 'rygmarv', ms: number | null): string {
  // IGN runde 1: "Volt: Jev 0,18 s" var udviklersprog. Modelnavnet står kun i ?debug-loggen.
  return by === 'jev' && ms !== null ? `Volt tænkte i ${secs(ms)}` : 'Volt på refleks';
}

/** Logbogen efter banen: "Jeg traf N beslutninger, tænkte typisk i 0,21 s, spurgte dig Y gange." */
export function logbookText(s: VoltStats): string {
  if (s.decisions === 0) return 'Jeg nåede ikke at gøre noget denne gang.';
  const n = `Jeg traf ${s.decisions} ${s.decisions === 1 ? 'beslutning' : 'beslutninger'}`;
  const med = s.medianMs !== null ? `, tænkte typisk i ${secs(s.medianMs)}` : ' på refleks';
  const asks = `spurgte dig ${s.asks} ${s.asks === 1 ? 'gang' : 'gange'}`;
  return `${n}${med}, ${asks}.`;
}

/** Det Volt selv fik gjort på banen (fra VoltSession). */
export interface VoltMoments {
  built: number;
  fixed: number;
  /** Banens bedste øjeblik, fx "Mine master holdt Ribe til Esbjerg gennem stormen." */
  best: string | null;
  /** Volts sidste replik, når banen sluttede. */
  final: string | null;
}

/** "Jeg byggede 4 ledninger og lappede 2." eller null, hvis Volt intet nåede. */
export function deedsText(m: VoltMoments): string | null {
  const parts: string[] = [];
  if (m.built > 0) parts.push(`byggede ${m.built} ${m.built === 1 ? 'ledning' : 'ledninger'}`);
  if (m.fixed > 0) parts.push(`reddede ${m.fixed} ${m.fixed === 1 ? 'gang' : 'gange'} et stykke net`);
  if (parts.length === 0) return null;
  return `Jeg ${parts.join(' og ')}.`;
}

const OUTCOME: Record<Decision['outcome'], string> = {
  pending: 'på vej',
  done: 'gjort',
  wait: 'venter',
  asked: 'spurgte',
  skipped: 'sprang over',
  claimed: 'din',
  failed: 'gik ikke',
};

export class VoltPanel {
  readonly el: HTMLElement;
  /** Markøren på kortet: hvor Volt vil bygge. */
  readonly marker: HTMLImageElement;
  private face: HTMLImageElement;
  private faceBtn: HTMLButtonElement;
  private src = h('span', { class: 'volt-src' });
  private dots = h('span', { class: 'volt-dots', 'aria-hidden': 'true' }, h('i'), h('i'), h('i'));
  private sayText = h('span', { class: 'volt-say-text' });
  private say = h('p', { class: 'volt-say', role: 'status', 'aria-live': 'polite' }, this.sayText);
  private next = h('div', { class: 'volt-next', hidden: true });
  private nextText = h('span', { class: 'volt-next-text' });
  private claimBtn: HTMLButtonElement | null = null;
  private nextBar = h('i', { class: 'volt-next-bar' });
  private askEl = h('div', { class: 'volt-ask', hidden: true, role: 'group', 'aria-label': 'Volt spørger' });
  private askOpts = h('div', { class: 'volt-ask-opts' });
  private askQ = h('span', { class: 'volt-ask-q' }, 'Hvad synes du?');
  private askNo = h('button', { type: 'button', class: 'volt-ask-no', hidden: true, onclick: () => this.handlers.answer('') }, 'Ikke nu');
  private askBar = h('i', { class: 'volt-ask-bar' });
  private drawer = h('div', { class: 'volt-drawer', id: 'volt-drawer' });
  private toggleBtn: HTMLButtonElement;
  private thoughtHead = h('p', { class: 'volt-th-head' });
  private thoughtList = h('ol', { class: 'volt-th-list' });
  private logList = h('ol', { class: 'volt-log' });
  private input: HTMLInputElement;
  private planEl = h('p', { class: 'volt-plan', hidden: true });
  private tags = h('div', { class: 'volt-tags', hidden: true, 'aria-label': 'Det har du bedt Volt om' });
  private sayUntil = 0;
  private mood: Mood = 'happy';
  private thinkingOn = false;
  private blinkAt = 0;
  private shownFace = '';
  private open = false;
  private asked: { until: number; ms: number } | null = null;
  private nextUntil: { at: number; ms: number } | null = null;
  /** Markørens glid: sidste mål, vist punkt og et igangværende glid mod et nyt mål. */
  private markAt: P | null = null;
  private markShown: P | null = null;
  private glide: { from: P; start: number } | null = null;

  constructor(
    private faces: VoltFaces,
    private handlers: VoltPanelHandlers,
  ) {
    this.face = h('img', { class: 'volt-face', src: faces.volt, alt: '', width: 64, height: 64, draggable: 'false' });
    this.faceBtn = h(
      'button',
      { type: 'button', class: 'volt-portrait', 'aria-controls': 'volt-drawer', 'aria-expanded': 'false', title: 'Volts tanker', onclick: () => this.setOpen(!this.open) },
      this.face,
      this.dots,
    );
    this.toggleBtn = h('button', { type: 'button', class: 'volt-toggle', 'aria-controls': 'volt-drawer', 'aria-expanded': 'false', onclick: () => this.setOpen(!this.open) }, 'Tanker');
    const claim = h('button', { type: 'button', class: 'volt-claim', onclick: () => this.handlers.claim() }, 'Jeg tager den');
    this.claimBtn = claim;
    this.next.append(h('span', { class: 'volt-next-row' }, this.nextText, claim), h('span', { class: 'volt-track', 'aria-hidden': 'true' }, this.nextBar));
    this.askEl.append(h('span', { class: 'volt-ask-row' }, this.askQ, this.askNo), this.askOpts, h('span', { class: 'volt-track', 'aria-hidden': 'true' }, this.askBar));

    this.input = h('input', {
      type: 'text',
      class: 'volt-input',
      maxlength: LIMITS.text,
      placeholder: 'Sig noget til Volt',
      'aria-label': 'Besked til Volt',
      enterkeyhint: 'send',
      autocomplete: 'off',
    });
    // Tasterne skal ikke nå spillets genveje, mens man skriver.
    this.input.addEventListener('keydown', (e) => e.stopPropagation());
    const form = h(
      'form',
      {
        class: 'volt-form',
        onsubmit: (e: Event) => {
          e.preventDefault();
          const t = this.input.value.trim();
          if (!t) return;
          this.handlers.instruct(t, false);
          this.input.value = '';
          this.input.blur();
        },
      },
      this.input,
      h('button', { type: 'submit', class: 'volt-send' }, 'Send'),
    );
    const chips = h(
      'div',
      { class: 'volt-chips' },
      ...VOLT_CHIPS.map((c) => h('button', { type: 'button', class: 'volt-chip', onclick: () => this.handlers.instruct(c, true) }, c)),
    );
    this.drawer.append(
      chips,
      form,
      this.planEl,
      h('p', { class: 'volt-sub' }, 'Volt tænker'),
      this.thoughtHead,
      this.thoughtList,
    );
    // Loggen med hjerne, svartid og udfald er til udviklere: kun med ?debug i adressen.
    if (DEBUG_LOG) this.drawer.append(h('p', { class: 'volt-sub volt-log-sub' }, 'Log'), this.logList);
    this.el = h(
      'section',
      { class: 'volt', hidden: true, 'aria-label': 'Volt, din AI-makker' },
      h('div', { class: 'volt-top' }, this.faceBtn, h('div', { class: 'volt-col' }, h('div', { class: 'volt-row' }, this.src, this.toggleBtn), this.say)),
      this.tags,
      this.next,
      this.askEl,
      this.drawer,
    );
    this.marker = h('img', { class: 'volt-marker', src: faces.marker, alt: '', hidden: true, 'aria-hidden': 'true', draggable: 'false' });
    this.brain('jev', null);
    this.setOpen(false);
  }

  /** Fold tankepanelet ud eller ind. */
  setOpen(on: boolean): void {
    this.open = on;
    toggle(this.el, 'open', on);
    this.faceBtn.setAttribute('aria-expanded', String(on));
    this.toggleBtn.setAttribute('aria-expanded', String(on));
    setText(this.toggleBtn, on ? 'Skjul' : 'Tanker');
  }

  get isOpen(): boolean {
    return this.open;
  }

  /**
   * Fold sammen til portrættet, mens en by i nød står bag panelet (runde 2: "Viborg gik sort bag
   * Volts chip"). Det fulde panel kommer igen, når byen er reddet.
   */
  tuck(on: boolean): void {
    toggle(this.el, 'tuck', on);
  }

  get tucked(): boolean {
    return this.el.classList.contains('tuck');
  }

  show(on: boolean): void {
    this.el.hidden = !on;
    if (!on) this.marker.hidden = true;
  }

  /** Ny bane: tom log, ingen boble, ingen spørgsmål. */
  reset(): void {
    this.thoughtList.replaceChildren();
    this.logList.replaceChildren();
    setText(this.thoughtHead, 'Volt har ikke tænkt endnu.');
    this.planEl.hidden = true;
    this.policy(null);
    this.clearAsk();
    this.next.hidden = true;
    this.nextUntil = null;
    this.say.hidden = true;
    this.sayUntil = 0;
    this.thinking(false);
    this.brain('jev', null);
  }

  // -------------------------------------------------------------------------
  // VoltUi

  thinking(on: boolean): void {
    this.thinkingOn = on;
    toggle(this.el, 'thinking', on);
  }

  brain(by: 'jev' | 'rygmarv', ms: number | null): void {
    setText(this.src, by === 'jev' && ms === null ? 'Volt' : brainChip(by, ms));
    toggle(this.src, 'jev', by === 'jev' && ms !== null);
    toggle(this.src, 'spine', by === 'rygmarv');
  }

  /** Står Volts taleboble fremme lige nu? */
  get speaking(): boolean {
    return !this.el.hidden && !this.say.hidden;
  }

  /** Noget vigtigere står fremme (messages.ts): boblen venter. Står den længere end sin tid, udløber den. */
  hold(on: boolean): void {
    toggle(this.say, 'held', on);
  }

  speak(line: string, mood: Mood, now: number): void {
    this.mood = mood;
    setText(this.sayText, line);
    setAttr(this.say, 'title', line);
    this.say.hidden = false;
    this.say.classList.remove('pop');
    void this.say.offsetWidth;
    this.say.classList.add('pop');
    // Ca. 0,35 s pr. ord, mindst 3,5 s.
    this.sayUntil = now + Math.max(3500, line.split(' ').length * 350 + 1500);
  }

  /** `claimable`: må spilleren selv tage trækket? Med roller kan en driftsvagt ikke bygge Volts ledning. */
  announce(c: VoltCandidate, inMs: number, by: Brain, now: number, claimable = true): void {
    this.clearAsk();
    setText(this.nextText, `${by === 'player' ? 'Som du vil:' : 'Mit træk:'} ${c.label}`);
    if (this.claimBtn) this.claimBtn.hidden = !claimable;
    this.next.hidden = false;
    this.nextUntil = { at: now + inMs, ms: Math.max(1, inMs) };
  }

  /** `proposal`: Volt bygger kun, hvis spilleren vælger; "Ikke nu" eller intet svar er et nej. */
  ask(top: VoltCandidate[], timeoutMs: number, now: number, proposal = false): void {
    this.next.hidden = true;
    setText(this.askQ, proposal ? 'Skal jeg bygge?' : 'Hvad synes du?');
    this.askNo.hidden = !proposal;
    this.askOpts.replaceChildren(
      ...top.map((c, i) =>
        h('button', { type: 'button', class: `volt-opt${i === 0 ? ' first' : ''}`, onclick: () => this.handlers.answer(c.cand.id) }, c.label),
      ),
    );
    this.askEl.hidden = false;
    this.asked = { until: now + timeoutMs, ms: timeoutMs };
    this.mood = 'worried';
  }

  clearAsk(): void {
    this.askEl.hidden = true;
    this.asked = null;
  }

  acted(): void {
    this.next.hidden = true;
    this.nextUntil = null;
  }

  /** Spillerens aktive ønsker som mærker med ×, så de kan ses og fjernes. */
  policy(p: Policy | null): void {
    const list = p ? policyTags(p) : [];
    const key = list.map((t) => t.key).join(',');
    if (this.tags.dataset.key === key) return;
    this.tags.dataset.key = key;
    this.tags.hidden = list.length === 0;
    this.tags.replaceChildren(
      ...list.map((t) =>
        h(
          'button',
          { type: 'button', class: 'volt-tag', title: 'Fjern ønsket', 'aria-label': `${t.text}, fjern`, onclick: () => this.handlers.unset(t.key) },
          h('span', {}, t.text),
          h('b', { 'aria-hidden': 'true' }, '×'),
        ),
      ),
    );
  }

  plan(note: string | null): void {
    this.planEl.hidden = !note;
    if (note) setText(this.planEl, `Plan: ${note}`);
  }

  /** Forslag, der ikke længere kan lade sig gøre (bygget, ingen plads, holdet fuldt), forsvinder fra listen. */
  prune(ok: (id: string) => boolean): void {
    for (const li of this.thoughtList.querySelectorAll<HTMLElement>('li[data-id]')) {
      const id = li.dataset.id ?? '';
      li.hidden = !li.classList.contains('chosen') && !id.startsWith('wait') && !ok(id);
    }
  }

  thought(d: Decision): void {
    const who = d.by === 'jev' && d.ms !== null ? `Jev ${secs(d.ms)}` : d.by === 'rygmarv' ? 'rygmarven' : 'dig';
    // Rygmarven har ingen sandsynligheder: dens scorer vises som andele (softmax), så tallene har
    // samme enhed (%) som Jevs. Overskriften siger, at det er rygmarvens vurdering.
    const shares = spineShares(d.cands.map((c) => c.score));
    const prob = (c: (typeof d.cands)[number], i: number): number => (c.p !== null ? c.p : (shares[i] ?? 0));
    const ps = d.cands.map((c, i) => ({ c, p: prob(c, i) }));
    // Tallet i overskriften er det valgte forslags egen bjælke, så de to aldrig modsiger hinanden.
    const chosen = ps.find((x) => x.c.id === d.choice);
    const conf = chosen ? `, ${Math.round(chosen.p * 100)} % ${d.by === 'rygmarv' ? 'af stemmerne' : 'sikker'}` : '';
    // Hjerne, svartid og sikkerhed er udviklersprog (playtest runde 3): kun med ?debug.
    setText(this.thoughtHead, DEBUG_LOG ? `Valgt af ${who}${conf}: ${OUTCOME[d.outcome]}` : `Volts valg: ${OUTCOME[d.outcome]}`);
    // Største øverst.
    const sorted = [...ps].sort((a, b) => b.p - a.p);
    this.thoughtList.replaceChildren(
      ...sorted.map(({ c, p: frac }) => {
        const pct = `${Math.round(frac * 100)} %`;
        return h(
          'li',
          { class: c.id === d.choice ? 'chosen' : '', 'data-id': c.id },
          h('span', { class: 'volt-th-label' }, c.label),
          h('span', { class: 'volt-th-p' }, pct),
          h('i', { class: 'volt-th-bar', style: `width:${Math.round(Math.min(1, frac) * 100)}%` }),
        );
      }),
    );
    // Loggen: én linje pr. beslutning, nyeste øverst. Samme beslutning opdateres på stedet, og den
    // samme handling flere gange i træk (typisk "Vent og spar") lægges sammen til én linje med ×N.
    const key = String(d.at);
    let li = this.logList.querySelector<HTMLElement>(`[data-at="${key}"]`);
    const first = this.logList.firstElementChild as HTMLElement | null;
    let n = Number(li?.dataset.n ?? '1');
    if (!li && first && first.dataset.label === d.label && first.dataset.outcome === d.outcome && d.outcome !== 'pending') {
      li = first;
      n = Number(first.dataset.n ?? '1') + 1;
      li.dataset.at = key;
    }
    if (!li) {
      li = h('li', { 'data-at': key });
      this.logList.prepend(li);
      while (this.logList.childElementCount > 8) this.logList.lastElementChild?.remove();
    }
    li.dataset.label = d.label;
    li.dataset.outcome = d.outcome;
    li.dataset.n = String(n);
    const ms = d.ms !== null ? ` ${d.ms} ms` : '';
    const times = n > 1 ? ` ×${n}` : '';
    li.replaceChildren(
      h('span', { class: 'volt-log-label' }, `${d.label}${times}`),
      h('span', { class: `volt-log-by ${d.by}` }, `${BRAIN_NAME[d.by]}${ms} · ${OUTCOME[d.outcome]}`),
    );
  }

  // -------------------------------------------------------------------------

  /** Hver frame: ansigt, nedtællinger, boblen og markøren. `at` er markørens skærmpunkt (eller null). */
  update(now: number, at: P | null): void {
    if (this.el.hidden) return;
    if (this.sayUntil && now > this.sayUntil) {
      this.say.hidden = true;
      this.sayUntil = 0;
      if (this.mood === 'worried' && !this.asked) this.mood = 'happy';
    }
    if (this.nextUntil) {
      const left = Math.max(0, this.nextUntil.at - now);
      this.nextBar.style.width = `${Math.round((left / this.nextUntil.ms) * 100)}%`;
    }
    if (this.asked) {
      const left = Math.max(0, this.asked.until - now);
      this.askBar.style.width = `${Math.round((left / this.asked.ms) * 100)}%`;
    }
    // Blink en gang imellem, så han virker levende.
    if (now > this.blinkAt + 4200) this.blinkAt = now;
    const blink = now - this.blinkAt < 140;
    const want = this.thinkingOn ? this.faces.think : blink ? this.faces.blink : this.mood === 'worried' || this.asked ? this.faces.worried : this.faces.volt;
    if (want !== this.shownFace) {
      this.shownFace = want;
      this.face.src = want;
    }
    this.marker.hidden = !at;
    if (!at) {
      this.markAt = this.markShown = this.glide = null;
      return;
    }
    // Et nyt mål (et spring på over 40 px fra sidste frame) glider på 280 ms; små skift fra
    // kameraet følges direkte, så markøren ikke slæber efter, når man panorerer.
    const last = this.markAt;
    const jump = last !== null && Math.hypot(at.x - last.x, at.y - last.y) > 40;
    const calm = document.documentElement.classList.contains('calm');
    if (jump && this.markShown && !calm) this.glide = { from: { ...this.markShown }, start: now };
    this.markAt = { x: at.x, y: at.y };
    let shown: P = this.markAt;
    if (this.glide) {
      const t = Math.min(1, (now - this.glide.start) / 280);
      const e = 1 - (1 - t) ** 3;
      shown = { x: this.glide.from.x + (at.x - this.glide.from.x) * e, y: this.glide.from.y + (at.y - this.glide.from.y) * e };
      if (t >= 1) this.glide = null;
    }
    this.markShown = shown;
    this.marker.style.transform = `translate(${Math.round(shown.x)}px, ${Math.round(shown.y)}px)`;
  }
}

/** Logbogen til resultatskærmen: bedste øjeblik, hvad Volt nåede, tallene og hans sidste ord. */
export function logbookEl(face: string, s: VoltStats, m?: VoltMoments): HTMLElement {
  // Ingen gentagelse: "Min første ledning" og "Jeg byggede 1 ledning" siger det samme (IGN runde 1).
  const same = !!m && m.built <= 1 && m.fixed === 0 && !!m.best?.startsWith('Min første ledning');
  const deeds = m && !same ? deedsText(m) : null;
  const final = h('q', { class: 'volt-final', hidden: !m?.final }, m?.final ?? '');
  return h(
    'div',
    { class: 'volt-logbook' },
    h('img', { src: face, alt: '', width: 44, height: 44 }),
    h(
      'p',
      {},
      h('b', {}, 'Volts logbog'),
      m?.best ? h('span', { class: 'volt-best' }, `Bedste øjeblik: ${m.best}`) : null,
      deeds ? h('span', {}, deeds) : null,
      h('span', { class: 'volt-stats' }, logbookText(s)),
      final,
    ),
  );
}
